import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { parseSubscriptionType } from '@/atoms/subscription';
import useConversionDetails from '@/hooks/analytics/useConversionDetails';
import { usePricingOptions } from '@/hooks/api/usePricingOptions';
import { trackConversion } from '@/lib/utils';

import type { PricingByCurrency } from '@/hooks/api/usePricingOptions';

const normalizeLength = (
  raw: string | string[] | undefined
): 'Annual' | 'Monthly' | null => {
  const value = typeof raw === 'string' ? raw.toLowerCase() : null;
  if (value === 'annual') {
    return 'Annual';
  }
  if (value === 'monthly') {
    return 'Monthly';
  }
  return null;
};

/**
 * Fires the subscription purchase conversion for a download-journey checkout
 * (DEV-6516).
 *
 * Normally `useStripeCheckoutResponse` tracks this when the user lands on
 * `/music?success=true` via the generic success screen's "Start exploring".
 * Download-journey users skip that hop — they return to the page they came
 * from instead — so the same event has to be fired here.
 *
 * Plan and length are read from the Stripe return URL (the authoritative
 * record of what was bought), and `value` straight from pricing options, so
 * the event is accurate even though the post-checkout settings/role refetch
 * may not have landed yet (which would otherwise leave `useConversionDetails`
 * reading the pre-checkout free plan).
 */
export const useDownloadPurchaseConversion = () => {
  const router = useRouter();
  const { data: pricingOptions } = usePricingOptions();
  const {
    subscriptionType: fallbackType,
    subscriptionLength: fallbackLength,
    asPath,
    currencyCode,
    paymentType,
    value: fallbackValue,
  } = useConversionDetails();

  return useCallback(() => {
    const planParam =
      typeof router.query.plan === 'string' ? router.query.plan : null;
    const subscriptionType =
      (planParam ? parseSubscriptionType.parse(planParam) : null) ??
      fallbackType;
    const subscriptionLength =
      normalizeLength(router.query.length) ?? fallbackLength;

    const currencyKey = currencyCode.toLowerCase();
    const plan = pricingOptions?.find(
      (option) => option.title.toLowerCase() === subscriptionType.toLowerCase()
    );
    // `in` before the index so an unsupported currency falls through to
    // `fallbackValue` rather than the assertion masking a missing key (the
    // typed lookup would otherwise read as always-defined).
    const currencyPricing =
      plan && currencyKey in plan.pricing
        ? plan.pricing[currencyKey as keyof PricingByCurrency]
        : undefined;
    const value = currencyPricing
      ? subscriptionLength === 'Annual'
        ? currencyPricing.yearly
        : currencyPricing.monthly
      : fallbackValue;

    void trackConversion({
      subscriptionType,
      subscriptionLength,
      paymentType,
      asPath,
      currencyCode,
      value,
    });
  }, [
    router.query.plan,
    router.query.length,
    pricingOptions,
    fallbackType,
    fallbackLength,
    asPath,
    currencyCode,
    paymentType,
    fallbackValue,
  ]);
};
